import { FileUpload } from '@mui/icons-material';
import { Box, Button, CircularProgress } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { UploadHints } from '../../../components/upload-hints';
import { formatFileSize, isFileTooLarge, MAX_FILE_SIZE_MB } from '../../../config/upload';
import { useNotification } from '../../../hooks/use-notification';
import type {
  ExtractedSupplierCandidate,
  ExtractionResult,
  IGoodsReceivingService,
  Supplier,
} from '../../../services/goods-receiving';
import { GoodsReceivingService } from '../../../services/goods-receiving';
import {
  type ExtractedSupplierFormData,
  ExtractionTerminalActionService,
  type IExtractionTerminalActionService,
} from '../../../services/goods-receiving/extraction-terminal-action.service';
import { getNetworkSnapshot } from '../../../utils/network-info';
import { useAddPhotos, usePatchDraft } from '../hooks/use-draft-mutations';
import { goodsReceivingQueryKeys } from '../query-keys';
import { SupplierCandidatesDialog } from './supplier-candidates-dialog';
import { SupplierFallbackDialog } from './supplier-fallback-dialog';
import { SupplierFormDialog } from './supplier-form-dialog';
import {
  classifyUploadFailure,
  getHttpStatusFromError,
  resolveNetworkErrorRecovery,
} from './upload-failure-message';

interface UploadFailureContext {
  fileSize: number;
  fileType: string;
  durationMs: number;
}

interface SupplierPhotoExtractionProps {
  draftId: string | null;
  extractionStatus?: string;
  extractionResult?: ExtractionResult | null;
  supplier?: Supplier | null;
  onSupplierSelected: (supplier: Supplier) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  /** Move focus to the supplier autocomplete when the user opts to pick from the list. */
  onRequestSupplierListFocus?: () => void;
}

export function SupplierPhotoExtraction({
  draftId,
  extractionStatus,
  extractionResult,
  supplier,
  onSupplierSelected,
  onLoadingChange,
  onRequestSupplierListFocus,
}: SupplierPhotoExtractionProps) {
  const { t } = useTranslation('goodsReceiving');
  const { t: tCommon } = useTranslation();
  const { showError, showInfo, showWarning } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams] = useSearchParams();
  const terminalActionService = useInjection<IExtractionTerminalActionService>(
    ExtractionTerminalActionService,
  );
  const goodsReceivingService = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();

  const addPhotosMutation = useAddPhotos();
  const patchDraftMutation = usePatchDraft();

  const isUploading = addPhotosMutation.isPending;
  const isExtracting = extractionStatus === 'PENDING';
  const isLoading = isUploading || isExtracting;

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  // When arriving from the home "Carica merci" shortcut, open the file picker
  // immediately. ponytail: a programmatic click needs transient user activation;
  // if the browser dropped it after the async draft creation the button stays
  // as the visible fallback.
  const autoUploadFiredRef = useRef(false);
  useEffect(() => {
    if (searchParams.get('autoUpload') !== '1' || autoUploadFiredRef.current || !draftId) {
      return;
    }
    autoUploadFiredRef.current = true;
    fileInputRef.current?.click();
  }, [searchParams, draftId]);

  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<ExtractedSupplierFormData>({});
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const [candidates, setCandidates] = useState<ExtractedSupplierCandidate[]>([]);
  const [fallbackOpen, setFallbackOpen] = useState(false);
  const [fallbackVariant, setFallbackVariant] = useState<'no-ddt' | 'failed'>('no-ddt');

  // Fire side effects whenever the draft first becomes observable in a terminal
  // extraction state (COMPLETED/FAILED). We deliberately do NOT gate on a
  // PENDING → terminal transition: the backend runs `addPhotosAndExtract`
  // synchronously, so the frontend usually never observes the intermediate
  // PENDING state — its first refetch already lands on COMPLETED. We also need
  // to react on mount when the user resumes a draft whose extraction finished
  // in a previous session. The ref tracks the last terminal status we reacted
  // to so we don't re-open the dialog on every render.
  const reactedToStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (extractionStatus !== 'COMPLETED' && extractionStatus !== 'FAILED') {
      reactedToStatusRef.current = undefined;
      return;
    }
    if (reactedToStatusRef.current === extractionStatus) {
      return;
    }
    reactedToStatusRef.current = extractionStatus;

    const action = terminalActionService.derive({
      extractionStatus,
      supplier,
      extractionResult,
    });
    switch (action.kind) {
      case 'failed':
        setFallbackVariant('failed');
        setFallbackOpen(true);
        return;
      case 'supplier-matched':
        onSupplierSelected(action.supplier);
        showInfo(t('supplierFound', { name: action.supplier.name }));
        return;
      case 'supplier-candidates':
        setCandidates(action.candidates);
        setCandidatesOpen(true);
        return;
      case 'supplier-extracted':
        setFormData(action.data);
        setFormOpen(true);
        return;
      case 'no-ddt-recognized':
        setFallbackVariant('no-ddt');
        setFallbackOpen(true);
        return;
      case 'noop':
        return;
      default: {
        action satisfies never;
        return;
      }
    }
  }, [
    extractionStatus,
    supplier,
    extractionResult,
    onSupplierSelected,
    showInfo,
    t,
    terminalActionService,
  ]);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const notifyUploadFailure = (err: unknown, ctx: UploadFailureContext) => {
    const status = getHttpStatusFromError(err);

    const network = getNetworkSnapshot();
    const errorName = err instanceof Error ? err.name : 'unknown';
    Sentry.captureException(err, {
      tags: {
        'upload.endpoint': 'goods-receiving-photo-extraction',
        'upload.error_name': errorName,
        'upload.http_status': status !== undefined ? String(status) : 'none',
        'network.effective_type': network.effectiveType,
        'network.save_data': network.saveData === null ? 'unknown' : String(network.saveData),
        'network.online': String(network.online),
      },
      contexts: {
        upload: {
          fileSize: ctx.fileSize,
          fileType: ctx.fileType,
          durationMs: ctx.durationMs,
          httpStatus: status ?? null,
        },
        network: {
          effectiveType: network.effectiveType,
          downlink: network.downlink,
          rtt: network.rtt,
          saveData: network.saveData,
          online: network.online,
        },
      },
    });

    switch (classifyUploadFailure(status)) {
      case 'rate-limit':
        showWarning(t('photoExtractionRateLimit'));
        break;
      case 'file-too-large':
        showError(tCommon('common.fileErrors.fileTooLargeServer', { maxSize: MAX_FILE_SIZE_MB }));
        break;
      case 'network':
        showWarning(t('photoExtractionNetworkError'));
        break;
      case 'technical':
        showInfo(t('extractionTechnicalInfo'));
        break;
    }
  };

  // ky aborts the upload at 120s, but the server may have kept processing and
  // completed the extraction anyway. Before surfacing a "network error" toast
  // for a request the server actually finished, refetch the draft and check
  // whether it moved on despite the client-side timeout. fetchQuery writes
  // the fresh data into the same cache entry useWizardDraft reads, so a
  // PENDING result also re-arms its refetchInterval polling automatically.
  const isDraftAliveAfterNetworkError = async (id: string): Promise<boolean> => {
    try {
      const draft = await queryClient.fetchQuery({
        queryKey: goodsReceivingQueryKeys.draft(id),
        queryFn: () => goodsReceivingService.findById(id),
      });
      return resolveNetworkErrorRecovery(draft?.extractionStatus) === 'recovered';
    } catch {
      return false;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !draftId) {
      return;
    }

    if (isFileTooLarge(file)) {
      showError(
        t('common:fileErrors.fileTooLarge', {
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          maxSize: MAX_FILE_SIZE_MB,
        }),
      );
      resetFileInput();
      return;
    }

    const startedAt = performance.now();
    try {
      await addPhotosMutation.mutateAsync({
        draftId,
        files: [file],
        options: { extract: true },
      });
    } catch (err) {
      const isNetworkFailure = classifyUploadFailure(getHttpStatusFromError(err)) === 'network';
      if (isNetworkFailure && (await isDraftAliveAfterNetworkError(draftId))) {
        return;
      }
      notifyUploadFailure(err, {
        fileSize: file.size,
        fileType: file.type,
        durationMs: Math.round(performance.now() - startedAt),
      });
    } finally {
      resetFileInput();
    }
  };

  function handleSupplierCreated(supplier: { id: string; name: string }) {
    onSupplierSelected(supplier as Supplier);
  }

  // Assigning a candidate already in the registry skips the create-supplier
  // form entirely. We also patch the draft immediately (without navigating):
  // the duplicate-DDT check on the next step reads supplier/document/date off
  // the server-side draft row, and its effect only fires once
  // draft.supplier.id is set. Without this patch the warning would never fire
  // for suppliers chosen from this dialog (see PRSP-193 plan §10).
  function handleConfirmExistingCandidate(supplier: {
    id: string;
    name: string;
    vatNumber: string;
  }) {
    setCandidatesOpen(false);
    onSupplierSelected(supplier as Supplier);
    if (draftId) {
      patchDraftMutation.mutate({ id: draftId, data: { supplierId: supplier.id } });
    }
  }

  // No registry match for the chosen candidate — open the create-supplier
  // form prefilled from it. Never prefill vatNumber from a candidate that
  // already resolved to a different existingSupplier: the backend rejects a
  // VAT that belongs to another record with 409 vatBelongsToOther.
  function handleCreateNewFromCandidate(candidate: ExtractedSupplierCandidate) {
    setCandidatesOpen(false);
    const prefillsVat = !candidate.existingSupplier;
    setFormData({
      name: candidate.name ?? undefined,
      vatNumber: prefillsVat ? (candidate.vatNumber ?? undefined) : undefined,
      address: candidate.address ?? undefined,
      phone: candidate.phone ?? undefined,
      email: candidate.email ?? undefined,
      vatReviewReason: prefillsVat ? candidate.vatReviewReason : undefined,
    });
    setFormOpen(true);
  }

  function handleFallbackCreateNew() {
    setFallbackOpen(false);
    setFormData({});
    setFormOpen(true);
  }

  function handleFallbackChooseFromList() {
    setFallbackOpen(false);
    onRequestSupplierListFocus?.();
  }

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Button
        variant="outlined"
        startIcon={isLoading ? <CircularProgress size={20} /> : <FileUpload />}
        onClick={() => fileInputRef.current?.click()}
        disabled={isLoading || !draftId}
        fullWidth
        sx={{ mb: 2 }}
      >
        {isLoading ? t('extractingFromPhoto') : t('loadWithPhoto')}
      </Button>

      <UploadHints formats="JPG, PNG, WEBP, PDF" showAiDisclaimer />

      <SupplierFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onCreated={handleSupplierCreated}
        onUseExisting={handleConfirmExistingCandidate}
        initialData={formData}
      />

      <SupplierCandidatesDialog
        open={candidatesOpen}
        candidates={candidates}
        onClose={() => setCandidatesOpen(false)}
        onConfirmExisting={handleConfirmExistingCandidate}
        onCreateNew={handleCreateNewFromCandidate}
      />

      <SupplierFallbackDialog
        open={fallbackOpen}
        variant={fallbackVariant}
        onChooseFromList={handleFallbackChooseFromList}
        onCreateNew={handleFallbackCreateNew}
        onClose={() => setFallbackOpen(false)}
      />
    </Box>
  );
}
